/**
 * Network — jednotná správa publikovaných veřejných URL napříč nody.
 *
 * Read-only pohled nad `network.list`. Publish/unpublish patří do acm-self,
 * owner modulu nebo do project-qualified orchestrator flow; Network UI nesmí
 * být obecná brána pro volbu cílového hostu/portu.
 */
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { callWidgetTool } from '../../../widgets/scope';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { EmptyState } from '../../../components/ui/EmptyState';
import { StatCard, StatsGrid } from '../../../components/ui/Stats';
import {
  AlertCircle,
  CheckCircle2,
  Globe,
  Network as NetIcon,
  RefreshCw,
} from 'lucide-react';


let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  opts?: { silent?: boolean; label?: string },
) {
  return callWidgetTool(name, args, _widgetScope, opts) as Promise<T>;
}

interface NetworkEntry {
  url: string;
  service: string;
  instance_ref?: string;
  node_id: string;
  npm_host_id?: number;
  npm_instance?: string;
  cert_id?: number;
  ssl: boolean;
  desired: boolean;
  actual: boolean;
  ts: number;
}

interface ListResp {
  ok: boolean;
  count: number;
  entries: NetworkEntry[];
  this_node: string;
}

function NetworkOverview() {
  const { t } = useTranslation();

  const list = useQuery<ListResp>({
    queryKey: ['network-list'],
    queryFn: () => callMcp<ListResp>('network.list'),
    refetchInterval: 10_000,
  });

  const entries = list.data?.entries ?? [];
  const thisNode = list.data?.this_node ?? '?';
  const byNode = entries.reduce<Record<string, number>>((acc, e) => {
    acc[e.node_id] = (acc[e.node_id] ?? 0) + 1;
    return acc;
  }, {});
  const actualOk = entries.filter((e) => e.actual).length;
  const divergent = entries.filter((e) => e.desired !== e.actual).length;

  return (
    <div className='space-y-4'>
      <div className='flex justify-end items-start'>
        <div className='flex gap-2'>
          <Button variant='ghost' size='sm' onClick={() => list.refetch()}>
            <RefreshCw size={12} /> {t('network.refresh')}
          </Button>
        </div>
      </div>

      <StatsGrid>
        <StatCard label={t('network.statPublished')} value={entries.length} icon={<Globe size={14} />} />
        <StatCard
          label={t('network.statSynced')}
          value={`${actualOk} / ${entries.length}`}
          icon={<CheckCircle2 size={14} />}
        />
        <StatCard label={t('network.statDivergent')} value={divergent} icon={<AlertCircle size={14} />} />
        <StatCard label={t('network.statThisNode')} value={thisNode} icon={<NetIcon size={14} />} />
      </StatsGrid>

      {Object.keys(byNode).length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {Object.entries(byNode).map(([node, count]) => (
            <Badge key={node} variant={node === thisNode ? 'success' : 'info'} className='text-xs'>
              {node}: {count}
            </Badge>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('network.publishedUrls')}</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0
            ? (
              <EmptyState
                icon={<Globe size={32} />}
                title={t('network.emptyTitle')}
                description={t('network.emptyDesc')}
              />
            )
            : (
              <table className='w-full text-sm'>
                <thead className='text-xs text-gray-500 border-b border-gray-800'>
                  <tr>
                    <th className='text-left py-2'>{t('network.colUrl')}</th>
                    <th className='text-left'>{t('network.colService')}</th>
                    <th className='text-left'>{t('network.colOwner')}</th>
                    <th className='text-center'>{t('network.colSsl')}</th>
                    <th className='text-center'>{t('network.colStatus')}</th>
                    <th className='text-right'>{t('network.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.url} className='border-b border-gray-800/40'>
                      <td className='py-2'>
                        <a
                          href={`https://${e.url}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='font-mono text-xs text-blue-400 hover:text-blue-300'
                        >
                          {e.url}
                        </a>
                      </td>
                      <td className='text-xs text-gray-300'>
                        {e.service}{e.instance_ref ? `/${e.instance_ref}` : ''}
                      </td>
                      <td className='text-xs text-gray-400'>{e.node_id}</td>
                      <td className='text-center'>
                        <Badge variant={e.ssl ? 'success' : 'warning'} className='text-[10px]'>
                          {e.ssl ? 'TLS' : 'HTTP'}
                        </Badge>
                      </td>
                      <td className='text-center'>
                        <Badge variant={e.actual ? 'success' : 'warning'} className='text-[10px]'>
                          {e.actual ? 'OK' : 'syncing'}
                        </Badge>
                      </td>
                      <td className='text-right text-[10px] text-gray-500'>
                        {e.service === 'acm-self' ? 'ACM self' : t('network.manageInOwner', 'Správa v modulu')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>
    </div>
  );
}


export function NetworkOverviewPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <NetworkOverview />;
}
